import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import {
  appendMissingDecimalPart,
  decimalSeparator,
  formatStringNumber,
  stringNumberToNumber,
} from '../../services/common/formatNew';
import { FormField } from '../FormField/FormField';
import { FormFieldPropsBase } from './formfields.model';

type NumericFormFieldType = 'numeric' | 'money' | 'months' | 'km' | 'percentage' | 'cv' | 'emission' | 'weight';

interface NumericFormFieldProps extends FormFieldPropsBase {
  type: NumericFormFieldType;
  maximumFractionDigits?: number;
  isSuccess?: boolean;
  isError?: boolean;
  /**
   * - Handler to run on value change in onBlur handler.
   * - Will not run if input was interacted with but final value provided
   * was equal to the one existing before interaction.
   * - Will not run on programmatic change of the field value.
   */
  onValueChange?: (newValue: number, formValues: any) => void;
  isDisabled?: boolean;
  rounding?: number;
  maxValue?: number;
  minValue?: number;
  allowEmpty?: boolean;
}

const onKeydown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  currentDisplayValue: string,
  maximumFractionDigits: number
) => {
  const { value } = e.target as HTMLInputElement;
  const { key } = e;

  // Prevent multiple commas
  if ((key === ',' || key === '.') && value.includes(',')) {
    e.preventDefault();
    return;
  }

  if (key === 'Enter') {
    (e.target as HTMLInputElement).blur();
    return;
  }

  const hasDecimalSeparator = currentDisplayValue?.at(-1) === decimalSeparator;

  // prettier-ignore
  const allowedKeys = [
    '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
    'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab',
    ...(maximumFractionDigits > 0 && !hasDecimalSeparator ? [',', '.'] : [])
  ];

  if (!allowedKeys.includes(key)) {
    e.preventDefault();
  }
};

export const NumericFormField: FunctionComponent<NumericFormFieldProps> = ({
  labelTranslationKey = '',
  additionalLabelText = '',
  placeholderTranslationKey = '',
  fieldName,
  type,
  isDisabled,
  isSuccess,
  isReadonly,
  isError,
  maximumFractionDigits = 4,
  rounding = 0,
  onValueChange,
  optional = false,
  maxValue,
  minValue,
  infoTextTranslationKey = '',
  allowEmpty = false,
}): React.JSX.Element => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const [displayValue, setDisplayValue] = useState<string>('');
  const { t } = useTranslation();

  const getDisplayValue = (value: string | number) => {
    let newDisplayValue = value;
    const isString = typeof newDisplayValue === 'string';

    if (isString) {
      if (maximumFractionDigits) {
        // Remove more than one fraction indicator
        newDisplayValue = (newDisplayValue as string).replace(/(,.*),/g, '$1');
      } else {
        // If fraction set to 0 do not allow fraction at all
        newDisplayValue = (newDisplayValue as string).replace(/[^0-9]+/g, '');
      }
    }

    let newDisplayValueBeforeRounding = '';

    const _rounding = 0;

    if (isString && (newDisplayValue as string).includes(decimalSeparator)) {
      const trailingZeros = (newDisplayValue as string).match(/0+$/)?.[0] ?? '';

      // Separator should be appended only if all decimal digits are trailing zeros,
      // because only then formatting will remove them together with the separator.
      const shouldAppendDecimalSeparator =
        (newDisplayValue as string).split(decimalSeparator)[1].length === trailingZeros.length;

      newDisplayValueBeforeRounding =
        formatStringNumber(newDisplayValue, _rounding, maximumFractionDigits) +
        (shouldAppendDecimalSeparator ? decimalSeparator : '') +
        trailingZeros;
    } else {
      newDisplayValueBeforeRounding = formatStringNumber(newDisplayValue, _rounding, maximumFractionDigits);
    }

    return newDisplayValueBeforeRounding;
  };

  const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    value = value.replace(/\./g, ',');
    if (type === ('emission' as NumericFormFieldType)) {
      value = value.replace(/\D/g, '').slice(0, 3); //  emission: max. 3 digits
    }
    setDisplayValue(getDisplayValue(value));
  };

  const _onBlur = () => {
    const displayValueBeforeRounding = displayValue;
    const roundedDisplayValue = formatStringNumber(displayValueBeforeRounding, rounding, maximumFractionDigits);

    if (allowEmpty && displayValue === '') {
      setFieldTouched(fieldName, true);
      return;
    }

    let newNumericValue = stringNumberToNumber(roundedDisplayValue, rounding);
    newNumericValue = maxValue ? Math.min(maxValue, newNumericValue) : newNumericValue;
    newNumericValue = minValue ? Math.max(minValue, newNumericValue) : newNumericValue;

    const currentNumericValue = stringNumberToNumber(get(values, fieldName, undefined));
    const isValidEmptyState = !allowEmpty || currentNumericValue !== 0;

    if (newNumericValue === currentNumericValue && isValidEmptyState) {
      setDisplayValue(appendMissingDecimalPart(getDisplayValue(newNumericValue), maximumFractionDigits, rounding));
      return;
    }
    setFieldTouched(fieldName, true);
    setFieldValue(fieldName, newNumericValue);

    if (onValueChange) {
      onValueChange(newNumericValue, values);
    }
  };

  useEffect(() => {
    const value = get(values, fieldName, undefined);
    // Visual value reset for cases when React will reuse
    // existing instance of the component for another field.
    if (['', undefined, null].includes(value) || Number.isNaN(value)) {
      setDisplayValue('');
    } else {
      setDisplayValue(appendMissingDecimalPart(getDisplayValue(value), maximumFractionDigits, rounding));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, fieldName]);

  let addonText;
  switch (type) {
    case 'km':
      addonText = 'km';
      break;
    case 'money':
      addonText = '€';
      break;
    case 'months':
      addonText = 'months';
      break;
    case 'percentage':
      addonText = '%';
      break;
    case 'cv':
      addonText = 'CV';
      break;
    case 'emission':
      addonText = 'g/km';
      break;
    case 'numeric':
      addonText = undefined;
      break;
    case 'weight':
      addonText = 'kg';
      break;
    default:
      addonText = undefined;
  }

  return (
    <FormField
      type="input"
      name={fieldName}
      testId={fieldName}
      labelText={`${t(labelTranslationKey)}${additionalLabelText}`}
      required={!optional && !isReadonly}
      infoText={t(infoTextTranslationKey)}
      disabled={isDisabled}
      render={(fieldProps) => {
        return (
          <Input
            {...fieldProps}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            placeholder={t(placeholderTranslationKey)}
            onChange={_onChange}
            onBlur={_onBlur}
            onKeyDown={(e) => onKeydown(e, displayValue, maximumFractionDigits)}
            value={displayValue}
            addonText={addonText}
            success={isSuccess}
            readOnly={isReadonly}
            stateIcon={isSuccess || isError}
            error={isError}
          />
        );
      }}
    />
  );
};
